import React from 'react';
import TrafficLandingTemplate, { LandingPageConfig } from './TrafficLandingTemplate';
import { Activity, BarChart3, Globe } from 'lucide-react';

const config: LandingPageConfig = {
    slug: 'ro/learn/what-is-social-traffic',
    titleTag: 'What is Social Traffic? The Ultimate Guide 2026 | Traffic Creator',
    metaDescription: 'Understand the difference between social, referral, and organic traffic. Learn how to optimize your traffic sources to improve SEO and site authority.',
    keywords: 'What is Social Traffic, Referral Traffic Definition, Types of Website Traffic, Bot Traffic vs Real Traffic, Educational Traffic Guide',
    primaryKeyword: 'What is Social Traffic',
    secondaryKeyword: 'Referral Traffic Definition',
    reviewCount: '465',
    ratingValue: '4.9',

    h1: 'What Is Social & Referral Traffic? - The Ultimate Educational Guide',
    subheadline: 'If you want to understand referral traffic definitions and what social traffic really means for your website, our guide makes it easier than ever.',

    problemH2: 'Why Confusing Traffic Types is Holding You Back',
    problemText: 'A large part of website analytics is properly attributing where your visitors come from. When you confuse organic traffic, direct traffic, referral traffic, and social traffic, you misallocate marketing budgets and hurt your long-term SEO strategy.',
    solutionPoints: [
        'Clearly define the major types of website traffic: Social, Referral, Direct, and Organic',
        'Learn how bot traffic vs. real traffic impacts your Google Analytics 4 (GA4) data',
        'Understand exactly how Traffic Creator can safely simulate social and referral channels',
        'Discover the SEO benefits of diversifying your traffic sources properly'
    ],

    featuresH2: 'Understanding the Types of Website Traffic',
    features: [
        { icon: <Globe />, title: 'Social Traffic', description: 'What is social traffic? It refers to visitors arriving from platforms like Facebook, Twitter, Instagram, or YouTube. A high percentage shows strong brand presence.' },
        { icon: <Activity />, title: 'Referral Traffic Definition', description: 'Referral traffic is any visit arriving from an external site (excluding search engines and social networks). It indicates your content is trusted and linked heavily.' },
        { icon: <BarChart3 />, title: 'Bot Traffic vs Real Traffic', description: 'Good bots (like Googlebot) crawl your site. Bad bots try to ruin analytics. Real residential traffic mimics human behavior safely to boost metrics.' }
    ],

    testimonialsH2: 'What Our Clients Say About Traffic Creator',
    testimonials: [
        { name: 'David M.', role: 'Digital Marketer', result: 'Balanced Analytics', text: 'I used to struggle with 100% direct traffic. Traffic Creator allowed me to buy targeted referral and social traffic, balancing my GA4 profile beautifully.', seed: 'davidm' },
        { name: 'Sarah K.', role: 'SEO Agency', result: 'Improved Trust', text: 'Understanding the referral traffic definition helped me explain to my clients why they needed diversified traffic sources. Traffic Creator delivers exactly that.', seed: 'sarahk' },
        { name: 'Jessica T.', role: 'Blog Owner', result: 'Safe Traffic', text: 'I was worried about bot traffic vs real traffic until I read this guide. The residential IPs from Traffic Creator are so safe and effective for SEO.', seed: 'jessicat' }
    ],

    ctaH2: 'Ready to Boost Your Social and Referral Metrics?',

    faqs: [
        { question: 'What exactly is social traffic and why do I need it?', answer: 'Social traffic is defined as visitors finding your website from social media platforms. You need it because search engines treat strong social signals and traffic as a metric of authority and popularity.' },
        { question: 'What is the standard referral traffic definition?', answer: 'Referral traffic occurs when a user clicks a link on another site (not a search engine) and lands on yours. It shows your site is being discussed across the web.' },
        { question: 'How is bot traffic different from real residential traffic?', answer: 'Dangerous bot traffic comes from datacenter IPs and shows 100% bounce rates. Real residential traffic uses household IPs, scrolls pages, clicks links, and appears completely natural to analytics.' },
        { question: 'Can Traffic Creator generate social and referral traffic?', answer: 'Yes! Our custom campaigns let you set the referrer. You can enter URLs like facebook.com or Reddit.com so the traffic appears as social or referral in your Google Analytics.' },
        { question: 'Is it safe to buy social and referral traffic?', answer: 'Yes, provided you use high-quality residential IPs (like Traffic Creator uses). We bypass bot detectors and look completely natural, keeping your site safe while boosting its metrics.' }
    ],

    relatedLinks: [
        { label: 'Social Media Traffic Signals', to: '/ro/social-media-traffic-bot' },
        { label: 'Website Traffic Generator', to: '/ro/website-traffic-generator' },
        { label: 'High Quality Traffic', to: '/ro/high-quality-website-traffic' },
    ],

    productName: 'What is Social & Referral Traffic? | The Ultimate Guide 2026',
    productDescription: 'Understand the difference between social, referral, and organic traffic. Learn how to optimize your traffic sources to improve your SEO and site authority.',
    translations: {
        'navFeatures': 'Caracteristici',
        'navPricing': 'Prețuri',
        'navFaq': 'FAQ',
        'navLogin': 'Autentificare Panou',
        'heroBadge': 'Serviciu Premium de Trafic',
        'heroCtaPrimary': 'Încercați Gratuit',
        'heroCtaSecondary': 'Vedeți Prețurile',
        'heroTrust': 'Peste 10.000 de utilizatori au încredere',
        'heroMicrocopy': 'Nu este necesar un card de credit. 100% Sigur și Verificat prin Analytics.',
        'heroLiveLabel': 'Live',
        'heroTrafficIncrease': 'Creștere Trafic',
        'heroActiveLabel': 'Activ',
        'heroVisitorsServed': 'Vizitatori Livrați',
        'socialProofLabel': 'Încredere de la:',
        'problemWithout': 'Fără',
        'problemWith': 'Cu',
        'problemBullets': [
                'Traficul organic scăzut vă limitează creșterea',
                'Clasamentele slabe în căutare afectează conversiile',
                'Concurenții vă domină cuvintele cheie',
                'Ratele mari de respingere dăunează SEO-ului',
        ],
        'statsCampaigns': 'Campanii Active Zilnic',
        'statsCountries': 'Țări',
        'statsUptime': 'Disponibilitate',
        'statsSupport': 'Suport',
        'featuresSubtitle': 'Tot ce aveți nevoie pentru a îmbunătăți performanța site-ului dvs.',
        'howItWorksH2': '3 Pași Simpli',
        'howItWorksSteps': [
                {
                        'title': 'Configurați Campania',
                        'desc': 'Alegeți țara, dispozitivul și setările de comportament în câteva minute.'
                },
                {
                        'title': 'Lansați Rețeaua',
                        'desc': 'Platforma noastră direcționează vizitatori reali către URL-ul dvs.'
                },
                {
                        'title': 'Analizați Rezultatele',
                        'desc': 'Urmăriți îmbunătățirea metricilor în Google Analytics 4.'
                },
        ],
        'pricingH2': 'Transparentă',
        'pricingSubtitle': 'Achiziție unică. Fără abonamente. Creditele nu expiră niciodată.',
        'pricingMostPopular': 'Cel Mai Popular',
        'pricingVisitors': 'vizitatori',
        'pricingGetStarted': 'Începeți Acum',
        'pricingGuarantee': 'Garanție de Returnare 30 de Zile',
        'pricingDelivery': 'Livrare Instantă (~5 min)',
        'pricingCountries': '190+ Țări',
        'faqH2': 'Întrebări Frecvente',
        'ctaSubtitle': 'Alăturați-vă celor peste 10.000 de clienți și creșteți traficul site-ului astăzi.',
        'ctaButtonPrimary': 'Începeți Gratuit',
        'ctaButtonSecondary': 'Vedeți Prețurile',
        'ctaMicrocopy': 'Nu este necesar un card de credit. 100% Sigur și Verificat prin Analytics.',
        'footerDescription': 'soluții pentru profesioniștii SEO și agențiile digitale din întreaga lume.',
        'footerRelatedServices': 'Servicii Conexe',
        'footerCompany': 'Companie',
        'footerAbout': 'Despre Noi',
        'footerBlog': 'Blog',
        'footerHelpCenter': 'Centru de Ajutor',
        'footerPrivacy': 'Politica de Confidențialitate',
        'footerImpressum': 'Informații Legale',
        'footerStatus': 'Toate Sistemele Funcționale',
        'footerBuyTraffic': 'Cumpărați Trafic Web',
    },
};

const RoWhatIsSocialTraffic: React.FC = () => {
    return <TrafficLandingTemplate config={config} />;
};

export default RoWhatIsSocialTraffic;
